import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  Modal,
  Pagination,
  Select,
} from '@nexora/design-system';
import { useClosingPortal } from './useClosing';
import {
  ATIVIDADE_PORTAL_LABEL,
  type PortalCliente,
} from './types';
import { formatCount, formatCurrency, formatDateTime, formatDocument } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Portal do Contador (multi-empresa)
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido (rota real /api/v1/closing):

   GET /closing/portal { search?, statusFiscal?, page?, limit? }
     → ClosingPortal (Paginated<PortalCliente> + resumoPorEmpresa):
       data: [{ clienteCnpj, clienteNome, empresaNome, empresaCnpj,
                statusFiscal, notasEmitidas, pendencias, valorMovimento,
                ultimaAtividade, ultimaAtividadeEm, atividades: [{
                tipo (FECHAMENTO_FISCAL|INTEGRACAO_CONTABIL|LOTE_GUIAS|
                DOCS_BAIXA|NOTAS_EMITIDAS), descricao, ocorridoEm }] }]
       resumoPorEmpresa: [{ companyId, empresaNome, empresaCnpj, clientes,
                            totalNotasEmitidas, totalPendencias }]

   Colunas da tela de referência: Cliente/CNPJ, Status Fiscal, Notas
   Emitidas, Pendências, Ações + o bloco "Últimas Atividades" (abre o modal
   com a timeline `atividades` de cada cliente). O filtro de Status Fiscal é
   real (query param `statusFiscal`); as opções vêm dos valores presentes na
   resposta (nunca inventadas).
   ═══════════════════════════════════════════════════════════════════════════ */

/** Mapeia o texto livre de statusFiscal para um badge visual, sem inventar
    vocabulário: só reconhece padrões óbvios (REGULAR/IRREGULAR/PENDENTE). */
function statusBadge(status: string | null): 'success' | 'warning' | 'error' | 'neutral' {
  if (!status) return 'neutral';
  if (/regular/i.test(status)) return 'success';
  if (/irregular|pendente|atras/i.test(status)) return 'error';
  if (/atenc|domiciliado/i.test(status)) return 'warning';
  return 'neutral';
}

export function ContadorPortalPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<PortalCliente | null>(null);

  const portalQuery = useClosingPortal({
    search: searchApplied || undefined,
    statusFiscal: statusFilter || undefined,
    page,
    limit: 20,
  });

  const clientes = portalQuery.data?.data ?? [];
  const totalPages = portalQuery.data?.totalPages ?? 1;
  const resumoPorEmpresa = portalQuery.data?.resumoPorEmpresa ?? [];

  /* Opções de Status Fiscal a partir dos valores reais da resposta. */
  const statusOptions = useMemo(() => {
    const vistos = new Set<string>();
    clientes.forEach((c) => {
      if (c.statusFiscal) vistos.add(c.statusFiscal);
    });
    return [...vistos];
  }, [clientes]);

  const handleSearch = (): void => {
    setSearchApplied(search.trim());
    setPage(1);
  };

  const handleStatusFilter = (value: string): void => {
    setStatusFilter(value);
    setPage(1);
  };

  return (
    <div className="contador-portal-page">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-1 uppercase tracking-wider">
            Fechamento Mensal / Escritório
          </p>
          <h1 className="font-display-lg text-display-lg text-primary m-0">Portal do Contador</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Situação fiscal de todos os clientes da carteira, notas emitidas e pendências.
          </p>
        </div>
      </div>

      {/* Erro */}
      {portalQuery.error && (
        <Card variant="outlined" padding="1.5rem" className="mb-lg">
          <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erro ao carregar o portal</p>
          <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f', marginTop: '0.25rem' }}>
            {portalQuery.error instanceof Error ? portalQuery.error.message : 'Erro desconhecido'}
          </p>
          <Button variant="primary" size="sm" onClick={() => portalQuery.refetch()} style={{ marginTop: '0.75rem' }}>
            Tentar novamente
          </Button>
        </Card>
      )}

      {/* Resumo por empresa */}
      {!portalQuery.isLoading && !portalQuery.error && resumoPorEmpresa.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-md mb-lg">
          {resumoPorEmpresa.map((emp) => (
            <Card key={emp.companyId} variant="outlined" padding="md">
              <p className="font-label-sm text-label-sm text-on-surface-variant m-0 uppercase tracking-wider">
                {emp.empresaNome ?? emp.companyId}
              </p>
              <p className="font-headline-md text-headline-md text-primary m-0" style={{ fontWeight: 700 }}>
                {formatCount(emp.clientes)} <span className="font-body-sm text-body-sm text-on-surface-variant font-normal">clientes</span>
              </p>
              <p className="font-body-sm text-body-sm text-on-surface-variant m-0">
                {formatCount(emp.totalNotasEmitidas)} notas · {formatCount(emp.totalPendencias)} pendências
              </p>
            </Card>
          ))}
        </div>
      )}

      {/* Filtros */}
      <Card variant="outlined" padding="1rem" className="mb-lg">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px' }}>
            <Input
              label="Buscar cliente (nome/CNPJ)"
              placeholder="Digite para filtrar"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
            />
          </div>
          {statusOptions.length > 0 && (
            <div style={{ flex: '0 0 220px' }}>
              <Select
                label="Status Fiscal"
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                selectSize="md"
              >
                <option value="">Todos os status</option>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <Button variant="secondary" size="md" onClick={handleSearch}>
            Buscar
          </Button>
          {(searchApplied || statusFilter) && (
            <Button
              variant="ghost"
              size="md"
              onClick={() => {
                setSearch('');
                setSearchApplied('');
                setStatusFilter('');
                setPage(1);
              }}
            >
              Limpar
            </Button>
          )}
        </div>
      </Card>

      {/* Tabela de clientes */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {portalQuery.data ? `${formatCount(portalQuery.data.total)} clientes` : ''}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Clientes da carteira</h3>
        </CardHeader>

        {portalQuery.isLoading && (
          <div style={{ padding: '2rem' }}>
            <LoadingSkeleton count={5} height="40px" borderRadius="8px" />
          </div>
        )}

        {!portalQuery.isLoading && !portalQuery.error && clientes.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p className="m-0" style={{ fontSize: '0.875rem' }}>
              Nenhum cliente encontrado com os filtros atuais.
            </p>
          </div>
        )}

        {!portalQuery.isLoading && !portalQuery.error && clientes.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Cliente', 'CNPJ', 'Status Fiscal', 'Notas Emitidas', 'Pendências', 'Última Atividade', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: ['Notas Emitidas', 'Pendências'].includes(h) ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clientes.map((c) => (
                  <tr key={c.id}>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 600 }}>
                      {c.clienteNome ?? c.empresaNome ?? '—'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                      {formatDocument(c.clienteCnpj ?? c.empresaCnpj)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      {c.statusFiscal ? (
                        <Badge variant={statusBadge(c.statusFiscal)} size="sm">
                          {c.statusFiscal}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right' }}>
                      {formatCount(c.notasEmitidas)}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem 1rem',
                        borderBottom: '1px solid #eceef0',
                        textAlign: 'right',
                        color: c.pendencias > 0 ? '#ba1a1a' : '#2e7d32',
                        fontWeight: 600,
                      }}
                    >
                      {formatCount(c.pendencias)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      {c.ultimaAtividade ? (
                        <>
                          <div>{c.ultimaAtividade}</div>
                          {c.ultimaAtividadeEm && (
                            <div className="font-label-sm text-label-sm text-on-surface-variant">
                              {formatDateTime(c.ultimaAtividadeEm)}
                            </div>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                      <Button variant="outline" size="sm" onClick={() => setDetail(c)}>
                        Atividades
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!portalQuery.isLoading && !portalQuery.error && totalPages > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </Card>

      {/* Modal de atividades */}
      {detail && (
        <Modal
          key={detail.id}
          isOpen
          onClose={() => setDetail(null)}
          title={detail.clienteNome ?? detail.empresaNome ?? 'Cliente'}
          size="md"
          footer={
            <Button variant="ghost" size="md" onClick={() => setDetail(null)}>
              Fechar
            </Button>
          }
        >
          <div className="mb-md">
            <div className="font-label-sm text-label-sm text-on-surface-variant">
              {detail.empresaNome ?? 'Empresa'} · CNPJ {formatDocument(detail.clienteCnpj ?? detail.empresaCnpj)}
            </div>
            {detail.valorMovimento != null && (
              <div className="font-headline-sm text-headline-sm text-on-surface" style={{ fontWeight: 700, marginTop: '0.25rem' }}>
                {formatCurrency(detail.valorMovimento)}
              </div>
            )}
          </div>
          <h4 className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider m-0 mb-sm">
            Últimas Atividades
          </h4>
          {detail.atividades.length === 0 ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant m-0">Nenhuma atividade registrada.</p>
          ) : (
            <div>
              {detail.atividades.map((atv) => (
                <div
                  key={atv.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.625rem 0',
                    borderBottom: '1px solid #eceef0',
                  }}
                >
                  <Badge variant="info" size="sm">
                    {ATIVIDADE_PORTAL_LABEL[atv.tipo] ?? atv.tipo}
                  </Badge>
                  <div style={{ flex: 1 }}>
                    <div className="font-body-sm text-body-sm text-on-surface">{atv.descricao}</div>
                    <div className="font-label-sm text-label-sm text-on-surface-variant">{formatDateTime(atv.ocorridoEm)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}